import _ from 'lodash';
import { computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, useState } from 'react';
import { withRouter } from 'react-router-dom';
import TableStore from 'stores/abstract/TableStore';
import styled from 'styled-components';

import {
  CheckCircleTwoTone,
  ExclamationCircleTwoTone,
  InfoCircleOutlined,
  PhoneOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Card, Col, Row, Tooltip, Input, Select, AutoComplete } from 'antd';
import { DoughnutChart } from 'shared/PitstopUI/PitstopChart';

import {
  PitstopButton,
  prescriptiveMaintenanceMessages,
  webServiceProvider,
} from 'shared';

import AmberWarningLamp from 'assets/icons/amberWarningLamp.svg?react';
import AmberWarningLampOff from 'assets/icons/amberWarningLampOff.svg?react';
import MilLamp from 'assets/icons/milLamp.svg?react';
import MilLampOff from 'assets/icons/milLampOff.svg?react';
import ProtectLamp from 'assets/icons/protectLamp.svg?react';
import ProtectLampOff from 'assets/icons/protectLampOff.svg?react';
import RedStopLamp from 'assets/icons/redStopLamp.svg?react';
import RedStopLampOff from 'assets/icons/redStopLampOff.svg?react';

import VMRSColorMap from 'shared/VMRSColorMap';

import {
  AppStore,
  CarStore,
  CurrentUserStore,
  IssueStore,
  ShopStore,
  UserStore,
} from 'stores';

import { DownloadVehicleHealthReport } from 'components/download/report';
import { CustomServiceForm } from 'containers/Form';

import { IssuesTable } from 'containers/Issues';

import { clearNumber } from 'components/WorkOrder/VMRS/utils';

const TextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LabelText = styled.p`
  margin: 3%
  font-weight: 700;
  align-self: center;
`;

const IconWrapper = styled.p``;

const ContentText = styled.p`
  flex: 1;
  margin: 3%
  align-self: center;
  color: black;
`;

const ActionsWrapper = styled.div`
  position: absolute;
  right: -7px;
  z-index: 4;
  display: flex;
  align-items: center;

  button {
    margin-right: 10px;
  }
`;

class IssuesPage extends Component {
  static propTypes = {
    car: PropTypes.object,
    location: PropTypes.object,
  };

  thisIssueStore = new TableStore();
  observableCar = null;

  state = {
    reloadTable: false,
    visible: false,
    category: 'new',
    vmrsData: null,
    recommendation: null, // Store the recommendation
    loadingRecommendation: false, // Track loading state for recommendation
    phonePrefix: '1',
    phoneInputValue: '',
    userList: [],
  };

  child = React.createRef();
  issueTableRef = React.createRef();

  constructor(props) {
    super(props);

    this.observableCar = observable.box(props.car);

    makeObservable(this, {
      engineLightStatus: computed,
      vehicleDetailReport: computed,
      observableCar: observable,
      issuesData: computed,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.car !== this.props.car) {
      this.observableCar.set(this.props.car);
    }
  }

  async componentDidMount() {
    this.observableCar.set(this.props.car);

    const userList = await UserStore.fetchShopUsers(ShopStore.currentShop.id);
    this.setState({ userList });

    if (!this.vehicleDetailReport.loaded) {
      await this.props.car.getVehicleDetailReport();
    }
    if (!this.engineLightStatus.loaded) {
      await this.props.car.getEngineLightStatus();
    }
    await this.fetchIssues();
    const newIssueIterator = this.thisIssueStore.data.values();
    const vmrs = [];
    const vmrsCounts = {};
    const vmrsFormatted = [];
    const VMRSValueToSys = {};
    for (let value of newIssueIterator) {
      if (value.vmrs) {
        VMRSValueToSys[_.get(value, 'vmrs.text').substring(0, 12)] = _.get(
          value,
          'vmrs.sys'
        );
        vmrs.push(_.get(value, 'vmrs.text').substring(0, 12));
      }
    }
    vmrs.forEach((el) => {
      vmrsCounts[el] = vmrsCounts[el] ? vmrsCounts[el] + 1 : 1;
    });
    for (let key in vmrsCounts) {
      vmrsFormatted.push({
        name: key,
        color: VMRSColorMap(VMRSValueToSys[key]),
        y: vmrsCounts[key],
      });
    }
    this.setState({ vmrsData: vmrsFormatted });
  }

  get vehicleDetailReport() {
    const car = this.observableCar.get();
    return car.detailReport;
  }

  get engineLightStatus() {
    const car = this.observableCar.get();
    const statusResponse = car.engineLightStatus;
    return statusResponse;
  }

  get issuesData() {
    if (CarStore.demo === false) {
      return {
        key: 'issues',
        value: [],
      };
    }

    const car = this.observableCar.get();
    const { issueCounts } = car;
    let value = [
      {
        name: 'critical',
        color: '#eb3d34',
        y: Number(issueCounts.critical) || 0,
      },
      {
        name: 'major',
        color: '#e28931',
        y: Number(issueCounts.major) || 0,
      },
      {
        name: 'minor',
        color: '#e2eb34',
        y: Number(issueCounts.minor) || 0,
      },
    ];

    return {
      key: '',
      seriesName: 'issues',
      emptyMessage: 'Your vehicle has no Issues',
      value,
    };
  }

  async fetchIssues() {
    await IssueStore.getIssuesForCarId(
      {
        filter: { status: 'new' },
        shopId: this.props.car.shopId,
        limit: 50,
      },
      this.thisIssueStore,
      this.props.car.id,
      false,
      true
    );
  }

  loadCategory = async (e) => {
    this.setState({
      category: e.target.value,
    });
  };

  saveService = async (values) => {
    try {
      AppStore.addLoading('Issue is being created...');

      const car = this.observableCar.get();

      const issue = await webServiceProvider.post('issue', {
        data: {
          action: values.action,
          userId: CurrentUserStore.user.userId,
          item: values.partname,
          priority:
            values.priority === 'Minor'
              ? 1
              : values.priority === 'Major'
                ? 2
                : values.priority === 'Critical'
                  ? 4
                  : undefined,
          description: values.description || 'No description available',
        },
        status: values.status || 'new',
        carId: car.id,
        issueType: 'customUser',
      });

      const data = {
        id: issue.id,
        action: values.action,
        source: 'user',
        name: values.partname + ' - ' + values.action,
        priority:
          values.priority === 'Minor'
            ? 1
            : values.priority === 'Major'
              ? 2
              : values.priority === 'Critical'
                ? 4
                : 'Minor',
        status: values.status || 'new',
      };

      this.setState({ reloadTable: true });
      IssueStore.addIssueToStore(issue.id, { ...data });
      await this.fetchIssues();

      AppStore.addSuccess('New Issue Added Successfully');
    } catch (e) {
      console.log('errorfetch', e);
      AppStore.addError(e.error);
      AppStore.addError('Sorry! New Issue Cannot Be Saved');
    } finally {
      console.log('finished');
      this.setState({ reloadTable: false });
      AppStore.removeLoading('Issue is being created...');
    }
  };

  renderChart = (data) => {
    return (
      <DoughnutChart
        chart={{
          height: 200,
          margin: [0, 200, 0, 0],
          legend: {
            align: 'right',
            layout: 'vertical',
            verticalAlign: 'top',
            width: 150,
            x: -30,
            y: 30,
          },
        }}
        data={data}
        loaded={CarStore.demo === false || this.vehicleDetailReport.loaded}
      />
    );
  };

  showModal = () => {
    this.setState({
      visible: true,
    });
  };
  handlePhoneInputChange = (value) => {
    this.setState({ phoneInputValue: value });
  };

  handlePhonePrefixChange = (value) => {
    this.setState({ phonePrefix: value });
  };

  handleIssueLoaded = async (issues) => {
    this.setState({ carIssues: issues });
    const carId = issues.length > 0 ? issues[0].car?.id : null;

    if (carId) {
      const promptData = {
        issues: issues,
      };

      // console.log('Extracted Car ID:', carId);

      const pms = await IssueStore.getPmForRecomendation(
        carId,
        this.props.car.shopId
      );

      if (pms && pms.length > 0) {
        promptData.pms = pms;
      }

      // console.log('promptData:', promptData);

      // Start loading
      this.setState({ loadingRecommendation: true });

      try {
        const recommendation =
          await IssueStore.getIssuesRecommendation(promptData);
        // console.log('Recommendation:', recommendation);

        // Store the recommendation in the state
        this.setState({ recommendation });
      } catch (error) {
        console.error('Error fetching recommendation:', error);
      } finally {
        // Stop loading
        this.setState({ loadingRecommendation: false });
      }
    } else {
      console.warn('No car ID found in the issues data.');
    }
  };

  handleCreate = (values) => {
    this.saveService(values);
    this.setState({ visible: false });
  };

  saveFormRef = (formRef) => {
    this.formRef = formRef;
  };

  handleCancel = (e) => {
    this.setState({
      visible: false,
    });
  };

  handleRequestCall = (e) => {
    const validatePhoneNumber = (number) => {
      return /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g.test(number);
    };

    const makeCall = async (number) => {
      const issuesList = await IssueStore.getCarIssuesList(
        {
          limit: 1000,
          filter: { status: 'new' },
          includeHistory: false,
        },
        this.props.car.id
      );
      const data = {
        recommendedAction:
          this.state.recommendation?.analysis?.recommendedAction,
        downtimeOptimizationTip:
          this.state.recommendation?.analysis?.downtimeOptimizationTip,
        issues: issuesList,
        customerName: CurrentUserStore.user.firstName,
        customerNumber: number,
        customerShopName: ShopStore.currentShop.name,
      };

      try {
        const response = await webServiceProvider.post('v1/vapi/make-call', {
          data: data,
        });
        const result = await response.json;
      } catch (e) {
        AppStore.addError(
          'There was an issue making the call. Please ensure you have a valid phone number and try again.'
        );
      }
    };

    e.preventDefault();

    const { phonePrefix, phoneInputValue } = this.state;
    const fullPhoneNumber = `+${phonePrefix}${phoneInputValue}`;
    if (
      fullPhoneNumber &&
      validatePhoneNumber(fullPhoneNumber) &&
      fullPhoneNumber.length === 12
    ) {
      console.log(`Phone number: ${fullPhoneNumber}`);
      AppStore.addSuccess(`Calling ${fullPhoneNumber}`);
      makeCall(fullPhoneNumber);
    } else {
      AppStore.addError('Please enter a valid phone number.');
    }
  };

  render() {
    const prefixSelector = (
      <Select
        value={this.state.phonePrefix}
        style={{ width: 60 }}
        onChange={this.handlePhonePrefixChange}
      >
        <Select.Option value="1">+1</Select.Option>
        {/* Add more country codes if needed */}
      </Select>
    );

    const userPhones = this.state.userList
      .filter((user) => !!user.phone)
      .map((user) => ({
        value: user.phone,
        label: `${user.firstName} ${user.lastName}: ${user.phone}`,
      }));

    const { recommendation, loadingRecommendation } = this.state;

    const car = this.observableCar.get();

    const engineLightStatus = this.engineLightStatus;
    const engineLightDisplay =
      engineLightStatus.data && engineLightStatus.data.data.length > 0
        ? _.get(engineLightStatus, 'data.data[0].payload', null)
        : null;

    let vehicleDetail = this.vehicleDetailReport;
    let maxPriority, maxAdjPriority, vehicleStatus, prescriptiveTextStyle;
    let prescriptiveIcon, maxMajorDate, routineServicePresent;

    if (_.get(vehicleDetail, 'data')) {
      maxAdjPriority = _.get(vehicleDetail, 'data.maxAdjPriority');
      maxPriority = _.max([
        maxAdjPriority,
        _.get(vehicleDetail, 'data.maxPriority'),
      ]);
      maxMajorDate = _.get(vehicleDetail, 'data.maxMajorIssueDate');
      routineServicePresent = _.get(
        vehicleDetail,
        'data.routineServicePresent'
      );
      if (maxPriority === 5 || maxPriority === 4) {
        vehicleStatus = prescriptiveMaintenanceMessages(
          maxPriority,
          maxMajorDate,
          routineServicePresent
        );
        prescriptiveTextStyle = { color: '#f92d2e' };
        prescriptiveIcon = (
          <ExclamationCircleTwoTone
            twoToneColor="#f92d2e"
            style={{ fontSize: '30px' }}
          />
        );
      } else if (maxPriority === 3 || maxPriority === 2) {
        vehicleStatus = prescriptiveMaintenanceMessages(
          maxPriority,
          maxMajorDate,
          routineServicePresent
        );
        prescriptiveTextStyle = { color: '#e28931' };
        prescriptiveIcon = (
          <ExclamationCircleTwoTone
            twoToneColor="#e28931"
            style={{ fontSize: '30px' }}
          />
        );
      } else if (maxPriority === 1 || maxPriority === 0) {
        vehicleStatus = prescriptiveMaintenanceMessages(
          maxPriority,
          maxMajorDate,
          routineServicePresent
        );
        prescriptiveTextStyle = { color: '#dec431' };
        prescriptiveIcon = (
          <ExclamationCircleTwoTone
            twoToneColor="#dec431"
            style={{ fontSize: '30px' }}
          />
        );
      } else {
        vehicleStatus = prescriptiveMaintenanceMessages(
          maxPriority,
          maxMajorDate,
          routineServicePresent
        );
        prescriptiveTextStyle = { color: 'green' };
        prescriptiveIcon = (
          <CheckCircleTwoTone
            twoToneColor="green"
            style={{ fontSize: '30px' }}
          />
        );
      }
    }

    return (
      <>
        <div style={{ display: 'flex' }}>
          {!_.isNil(maxPriority) || !_.isNil(maxAdjPriority) ? (
            <div style={{ maxWidth: '50%' }}>
              <Card
                title={
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    Recommendations
                    {/* <div style={{ marginLeft: 400 }}>
                      <Tooltip
                        title="sample"
                      >

                        <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <span style={{ color: '#1677FF', fontWeight: 200 }}>View Optimization Tip</span>
                          <InfoCircleOutlined style={{ marginLeft: 10, color: '#1677FF' }} />
                        </span>
                      </Tooltip>
                    </div> */}
                  </span>
                }
                headStyle={{
                  padding: '8px 16px',
                  fontSize: '16px', // Adjust font size as needed
                  borderBottom: 'none',
                  background: 'transparent',
                }}
                bodyStyle={{
                  paddingTop: 2,
                  paddingBottom: 16,
                  paddingLeft: 16,
                  paddingRight: 16,
                }}
                styles={{ body: prescriptiveTextStyle }}
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  height: 240,
                  maxWidth: '100%',
                  marginBottom: '1%',
                  padding: 'auto',
                }}
              >
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                  <TextRow>
                    <IconWrapper>{prescriptiveIcon}</IconWrapper>
                    <ContentText>
                      {loadingRecommendation ? (
                        'Loading recommendation...'
                      ) : recommendation ? (
                        <>
                          <p>{recommendation.analysis.recommendedAction}</p>
                          <p>
                            {recommendation.analysis.downtimeOptimizationTip}
                          </p>
                        </>
                      ) : (
                        vehicleStatus // Fallback to vehicleStatus
                      )}
                    </ContentText>
                  </TextRow>
                </div>
              </Card>
            </div>
          ) : null}
          {!_.isNil(engineLightDisplay) ? (
            <Card
              title={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Engine Lights
                  <Tooltip
                    title={
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>Red Stop: Stop Vehicle Immediately</li>
                        <li>
                          Malfunction Indicator Lamp (Mil): Issues with the
                          exhaust system
                        </li>
                        <li>Amber Check Engine: Vehicle needs service soon</li>
                        <li>Protect: Engine is derating to prevent damage</li>
                      </ul>
                    }
                    overlayStyle={{ minWidth: 342 }}
                  >
                    <InfoCircleOutlined
                      style={{ marginLeft: 10, color: '#888' }}
                    />
                  </Tooltip>
                </span>
              }
              headStyle={{
                padding: '8px 16px',
                fontSize: '16px', // Adjust font size as needed
                borderBottom: 'none',
                background: 'transparent',
              }}
              bodyStyle={{
                paddingTop: 12,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
              }}
              style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                height: 240,
                maxWidth: '28%',
                marginLeft: '2%',
                marginBottom: '1%',
                padding: 0,
              }}
            >
              <div
                style={{
                  whiteSpace: 'nowrap',
                  maxWidth: 350,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginTop: '-10px',
                }}
              >
                {/* Stop Lamp */}
                {!_.get(engineLightDisplay, 'generalVehicleWarningLight') && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 32,
                    }}
                  >
                    {_.get(engineLightDisplay, 'redStopLamp') ? (
                      <RedStopLamp width={25} height={25} />
                    ) : (
                      <RedStopLampOff width={25} height={25} />
                    )}
                    <span style={{ marginLeft: 20, fontWeight: 500, flex: 1 }}>
                      Stop Lamp
                    </span>
                    <span
                      style={{
                        width: 40,
                        display: 'inline-block',
                        textAlign: 'center',
                        color: _.get(engineLightDisplay, 'redStopLamp')
                          ? 'red'
                          : '#595959',
                        fontSize: 14,
                        fontWeight: _.get(engineLightDisplay, 'redStopLamp')
                          ? 'bold'
                          : 'normal',
                      }}
                    >
                      {_.get(engineLightDisplay, 'redStopLamp') ? 'On' : 'Off'}
                    </span>
                  </div>
                )}
                {/* Malfunction Lamp */}
                {!_.get(engineLightDisplay, 'generalVehicleWarningLight') && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 32,
                    }}
                  >
                    {_.get(engineLightDisplay, 'malfunctionLamp') ? (
                      <MilLamp width={25} height={25} />
                    ) : (
                      <MilLampOff width={25} height={25} />
                    )}
                    <span style={{ marginLeft: 20, fontWeight: 500, flex: 1 }}>
                      Malfunction Lamp
                    </span>
                    <span
                      style={{
                        width: 40,
                        display: 'inline-block',
                        textAlign: 'center',
                        color: _.get(engineLightDisplay, 'malfunctionLamp')
                          ? 'red'
                          : '#595959',
                        fontSize: 14,
                        fontWeight: _.get(engineLightDisplay, 'malfunctionLamp')
                          ? 'bold'
                          : 'normal',
                      }}
                    >
                      {_.get(engineLightDisplay, 'malfunctionLamp')
                        ? 'On'
                        : 'Off'}
                    </span>
                  </div>
                )}
                {/* Check Engine Lamp */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 32,
                  }}
                >
                  {_.get(engineLightDisplay, 'amberWarningLamp') ||
                  _.get(engineLightDisplay, 'generalVehicleWarningLight') ? (
                    <AmberWarningLamp width={25} height={25} />
                  ) : (
                    <AmberWarningLampOff width={25} height={25} />
                  )}
                  <span style={{ marginLeft: 20, fontWeight: 500, flex: 1 }}>
                    Check Engine Lamp
                  </span>
                  <span
                    style={{
                      width: 40,
                      display: 'inline-block',
                      textAlign: 'center',
                      color:
                        _.get(engineLightDisplay, 'amberWarningLamp') ||
                        _.get(engineLightDisplay, 'generalVehicleWarningLight')
                          ? 'red'
                          : '#595959',
                      fontSize: 14,
                      fontWeight:
                        _.get(engineLightDisplay, 'amberWarningLamp') ||
                        _.get(engineLightDisplay, 'generalVehicleWarningLight')
                          ? 'bold'
                          : 'normal',
                    }}
                  >
                    {_.get(engineLightDisplay, 'amberWarningLamp') ||
                    _.get(engineLightDisplay, 'generalVehicleWarningLight')
                      ? 'On'
                      : 'Off'}
                  </span>
                </div>
                {/* Protect Lamp */}
                {!_.get(engineLightDisplay, 'generalVehicleWarningLight') && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 32,
                    }}
                  >
                    {_.get(engineLightDisplay, 'protectWarningLamp') ? (
                      <ProtectLamp width={25} height={25} />
                    ) : (
                      <ProtectLampOff width={25} height={25} />
                    )}
                    <span style={{ marginLeft: 20, fontWeight: 500, flex: 1 }}>
                      Protect Lamp
                    </span>
                    <span
                      style={{
                        width: 40,
                        display: 'inline-block',
                        textAlign: 'center',
                        color: _.get(engineLightDisplay, 'protectWarningLamp')
                          ? 'red'
                          : '#595959',
                        fontSize: 14,
                        fontWeight: _.get(
                          engineLightDisplay,
                          'protectWarningLamp'
                        )
                          ? 'bold'
                          : 'normal',
                      }}
                    >
                      {_.get(engineLightDisplay, 'protectWarningLamp')
                        ? 'On'
                        : 'Off'}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          ) : null}
          {!_.isNil(maxPriority) ||
          !_.isNil(maxAdjPriority) ||
          !_.isNil(engineLightDisplay) ? (
            <Card
              title={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Talk to Shawn - AI Technician
                  <Tooltip
                    title="Get step-by-step diagnostic and repair assistance for this asset's issues."
                    overlayStyle={{ minWidth: 342 }}
                  >
                    <InfoCircleOutlined
                      style={{ marginLeft: 10, color: '#888' }}
                    />
                  </Tooltip>
                </span>
              }
              headStyle={{
                padding: '8px 16px',
                fontSize: '16px', // Adjust font size as needed
                borderBottom: 'none',
                background: 'transparent',
              }}
              bodyStyle={{
                paddingTop: 2,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
              }}
              style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                height: 240,
                maxWidth: '340px',
                marginLeft: '2%',
                marginBottom: '1%',
                padding: 0,
              }}
            >
              <TextRow>
                <PhoneOutlined
                  style={{
                    fontSize: '25px',
                    color: '#888',
                    alignSelf: 'flex-start',
                    marginTop: 5,
                  }}
                />
                <ContentText
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    alignSelf: 'center',
                    marginTop: 5,
                  }}
                >
                  Expect a call from +1 (833) 316 1507.
                </ContentText>
              </TextRow>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  padding: '0 0px',
                  marginTop: 12,
                }}
              >
                {/* <Input
                  addonBefore={prefixSelector}
                  placeholder="Enter phone number"
                  style={{ width: '100%' }}
                  onChange={(e) => {
                    const cleanedValue = clearNumber(e.target.value);
                    this.handlePhoneInputChange(cleanedValue);
                  }}
                  prefix=""
                  value={this.state.phoneInputValue}
                /> */}
                <Input.Group compact>
                  {prefixSelector}
                  <AutoComplete
                    showSearch
                    allowClear
                    style={{ width: '75%' }}
                    placeholder="Enter phone number"
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={userPhones}
                    value={this.state.phoneInputValue}
                    optionLabelProp="value"
                    onChange={(value) => {
                      if (value.startsWith('+')) {
                        if (!value.startsWith(`+${this.state.phonePrefix}`)) {
                          AppStore.addError(
                            'Please ensure the correct country code is selected from the dropdown. Only country codes in the dropdown are supported at the moment.'
                          );
                          this.handlePhoneInputChange('');
                          return;
                        }
                        value = value.slice(this.state.phonePrefix.length + 1);
                      }
                      value = value.replace(/\D/g, '');
                      this.handlePhoneInputChange(value);
                    }}
                    onSearch={(searchText) => {
                      this.handlePhoneInputChange(searchText);
                    }}
                    onClear={(searchText) => {
                      this.handlePhoneInputChange('');
                    }}
                    notFoundContent={null}
                  />
                </Input.Group>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={this.handleRequestCall}
                    style={{
                      background: '#123FBF',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      padding: '6px 0px',
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: 'pointer',
                      height: 32,
                      width: 113,
                    }}
                  >
                    Request Call
                  </button>
                </div>
              </div>
            </Card>
          ) : null}
        </div>
        {!(ShopStore.currentShop.id === 1177) ? (
          <ActionsWrapper>
            <PitstopButton ghost onClick={() => this.showModal()}>
              <PlusOutlined />
              Add Service
            </PitstopButton>
            <DownloadVehicleHealthReport
              carId={car.id}
              category={this.state.category}
            />
          </ActionsWrapper>
        ) : null}
        <CustomServiceForm
          childRef={(ref) => (this.child = ref)}
          wrappedComponentRef={this.saveFormRef}
          visible={this.state.visible}
          onCancel={this.handleCancel}
          onCreate={this.handleCreate}
          serviceName="customIssue"
        />

        <Row>
          <Col span={24}>
            {/*<IssueLegend />*/}
            <IssuesTable
              ref={(ref) => (this.issueTableRef = ref)}
              car={car}
              isEnabled={!_.isNil(car.id)}
              id={`car-${car.id}`}
              reload={this.state.reloadTable}
              fetchData={async (params, store) => {
                const fetchedIssues = await IssueStore.getIssuesForCarId(
                  {
                    ...params,
                    shopId: car.shopId,
                  },
                  store,
                  car.id
                );

                // console.log('✅ Issues fetched:', fetchedIssues); // You now have access
                this.handleIssueLoaded(fetchedIssues);
              }}
              page="carPage"
            />
          </Col>
        </Row>
      </>
    );
  }
}

export default withRouter(observer(IssuesPage));
